<!-- ============ HERO ============ -->
  <section class="k-hero">
    <div class="k-wrap k-hero-grid">
      <div class="k-hero-content k-reveal">
        <span class="k-eyebrow"><span class="dot"></span> KS3 Board-Based Focus: AQA, Edexcel, OCR &amp; Eduqas</span>
        <h1>Excel at KS3 With <span>Board-Specific Learning</span></h1>
        <p class="k-hero-tagline">
          A structured, exam-board-aligned KS3 programme covering Maths, English, Sciences and Languages — built on targeted study notes, weekly assessment, and a Result Analysis Report for every test.
        </p>
        <div class="k-btn-row">
          <a href="https://zfrmz.in/CXWHedw5dEnsTL7d361m" class="k-btn-primary">Enroll for KS3</a>
          <a href="https://www.sehyog.co.uk/freedemo" class="k-btn-outline">Book a Free Demo <i class="ti ti-arrow-right"></i></a>
        </div>
        <div class="k-stat-row">
          <div class="k-stat-chip">
            <span class="ic"><i class="ti ti-calendar-stats"></i></span>
            <span><span class="num">10+ Years</span><br><span class="lbl">Empowering KS3 Learners</span></span>
          </div>
          <div class="k-stat-chip">
            <span class="ic"><i class="ti ti-users"></i></span>
            <span><span class="num">10,000+</span><br><span class="lbl">Successful Students &amp; Growing</span></span>
          </div>
          <div class="k-stat-chip">
            <span class="ic"><i class="ti ti-calendar-event"></i></span>
            <span><span class="num">205 Sessions</span><br><span class="lbl">Covering All 5 Subjects</span></span>
          </div>
        </div>
      </div>
      <div class="k-hero-image k-reveal">
        <div class="frame">
          <img src="https://images.pexels.com/photos/35745597/pexels-photo-35745597.jpeg" alt="KS3 student at Sehyog Academy">
        </div>
        <div class="k-badge-float">
          <span class="ic"><i class="ti ti-certificate"></i></span>
          <span><span class="t1">Board-Specific Focus</span><br><span class="t2">AQA · Edexcel · OCR · Eduqas</span></span>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ ABOUT STRIP ============ -->
  <section class="k-section" style="padding-top:0;">
    <div class="k-wrap">
      <div class="k-about k-reveal">
        Achieving academic excellence together with honesty and transparency is the theme at Sehyog. We consistently produce the highest quality results and maximise young students' academic potential because of our experienced, passionate and dedicated <strong>KS3 team</strong>. We believe in continually pushing the boundaries using exciting technological aids with an effective &amp; transparent approach to achieve the desired results — earning excellent recommendations and parent reviews as we continue to attract new students to our proven KS3 preparation methodology.
      </div>
    </div>
  </section>

  <!-- ============ HOW SEHYOG ENHANCES LEARNING ============ -->
  <section class="k-section alt">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar" style="background:var(--k-cyan-dark);"></span>
        <span class="label">Our Teaching Approach</span>
      </div>
      <h2 class="k-title">How Sehyog Enhances Learning</h2>
      <p class="k-sub">Five proven teaching approaches, woven into every KS3 lesson to keep students engaged, curious, and confident.</p>

      <div class="k-enhance-grid">
        <div class="k-feature-card k-reveal">
          <span class="ic"><i class="ti ti-presentation"></i></span>
          <h3>Interactive Demonstrations</h3>
          <p>Interactive demonstrations, multimedia resources, and real-life examples introduce new concepts, making content relatable and memorable.</p>
        </div>
        <div class="k-feature-card k-reveal">
          <span class="ic"><i class="ti ti-users-group"></i></span>
          <h3>Collaborative Learning</h3>
          <p>Group work and collaborative activities let students solve problems and share knowledge together, promoting teamwork and peer learning.</p>
        </div>
        <div class="k-feature-card k-reveal">
          <span class="ic"><i class="ti ti-search"></i></span>
          <h3>Inquiry-Based Learning</h3>
          <p>Curiosity and critical thinking are fostered by encouraging students to ask questions and discover solutions through research and project-based tasks.</p>
        </div>
        <div class="k-feature-card k-reveal">
          <span class="ic"><i class="ti ti-stairs-up"></i></span>
          <h3>Scaffolded Learning</h3>
          <p>Support and guidance as students learn new concepts — starting simple and gradually increasing complexity with clear instructions and models.</p>
        </div>
        <div class="k-feature-card k-reveal">
          <span class="ic"><i class="ti ti-adjustments"></i></span>
          <h3>Differentiated Learning</h3>
          <p>Teaching adapted to diverse needs and abilities — extension activities for high achievers, and extra support for students who need it.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ SEHYOG METHODOLOGY ============ -->
  <section class="k-section">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar"></span>
        <span class="label">Sehyog Methodology</span>
      </div>
      <h2 class="k-title">The 4-Level Reinforcement Approach</h2>
      <p class="k-sub">Every topic moves through four reinforcing stages — so learning sticks, and gaps get closed before they become a problem.</p>

      <div class="k-method-grid">
        <div class="k-method-card k-reveal">
          <span class="step-num">01</span>
          <h4>PLM — Pre Lecture Material</h4>
          <ul>
            <li>Study notes supplied before the subsequent lecture</li>
            <li>Motivates students to read the study notes in advance</li>
          </ul>
        </div>
        <div class="k-method-card k-reveal">
          <span class="step-num">02</span>
          <h4>Lecture / Class</h4>
          <ul>
            <li>High-level summary of the lecture</li>
            <li>Assess understanding with practical examples</li>
            <li>Interactive delivery using teaching aids</li>
            <li>Quiz, questions &amp; group discussions</li>
            <li>Summary video for revision</li>
          </ul>
        </div>
        <div class="k-method-card k-reveal">
          <span class="step-num">03</span>
          <h4>Assessment &amp; Analysis</h4>
          <ul>
            <li>Home test / worksheets</li>
            <li>Face-to-face or online unit/topic test</li>
            <li>Full mock test in a real exam-like environment</li>
            <li>Result Analysis Report (RAR) &amp; historical trend analysis</li>
            <li>Detailed marking scheme &amp; video solutions</li>
          </ul>
        </div>
        <div class="k-method-card k-reveal">
          <span class="step-num">04</span>
          <h4>Feedback &amp; Reinforcement</h4>
          <ul>
            <li>Student papers shared with parents for triangular review</li>
            <li>Parent-teacher meeting reviewing papers together</li>
            <li>Weak &amp; strong areas highlighted via individual error log</li>
            <li>Doubt-clearing sessions</li>
          </ul>
        </div>
      </div>
      <p class="k-method-note">* Board specific — mapped to AQA, Edexcel, OCR &amp; Eduqas specifications.</p>
    </div>
  </section>

  <!-- ============ SEHYOG STRATEGY ============ -->
  <section class="k-section alt">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar" style="background:var(--k-gold-dark);"></span>
        <span class="label">Sehyog Strategy</span>
      </div>
      <h2 class="k-title">Comprehensive Offerings Under One Umbrella</h2>
      <p class="k-sub">Seven connected pillars that work together across the whole KS3 journey.</p>

      <div class="k-strategy-grid">
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">01</span><h4>Targeted Study Notes</h4></div>
          <ul><li>Well-designed &amp; targeted notes</li><li>Effective learning resources</li></ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">02</span><h4>Interactive Lectures</h4></div>
          <ul>
            <li>Board-wise experienced teachers</li>
            <li>Activates prior learning</li>
            <li>High-level to in-depth concepts</li>
            <li>Quick lecture summary video</li>
          </ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">03</span><h4>All-Round Learning</h4></div>
          <ul>
            <li>Technological aids</li>
            <li>Individualised support</li>
            <li>Video of class recordings for reference</li>
          </ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">04</span><h4>Consistent Assessment</h4></div>
          <ul>
            <li>Class tests</li>
            <li>Home test</li>
            <li>Unit / topic test</li>
            <li>Full mock test</li>
          </ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">05</span><h4>Error Log &amp; Feedback</h4></div>
          <ul>
            <li>Parent teacher meeting (PTM)</li>
            <li>Self-assessment &amp; reflection</li>
            <li>Shared student progress</li>
            <li>Weak &amp; strong areas highlighted</li>
          </ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">06</span><h4>Past Papers &amp; Full Mock Tests</h4></div>
          <ul>
            <li>Result Analysis Report (RAR) to error log to feedback to PTM</li>
            <li>Free online practice test &amp; video solution</li>
          </ul>
        </div>
        <div class="k-strategy-card k-reveal">
          <div class="row-top"><span class="step-num">07</span><h4>Analysis, Feedback, Counselling &amp; Improve</h4></div>
          <ul>
            <li>Result Analysis Report (RAR)</li>
            <li>Reinforcement of weak areas</li>
            <li>Review / doubt-clearing sessions</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ RESULT ANALYSIS REPORT ============ -->
  <section class="k-section">
    <div class="k-wrap">
      <div class="k-rar k-reveal">
        <div class="k-rar-text">
          <span class="k-rar-tag">A Comprehensive Tool</span>
          <h2>Result Analysis Report (RAR)</h2>
          <p class="desc">Designed to give detailed insight into your child's test performance — every mock test comes with a full breakdown, not just a score.</p>
          <ul class="k-rar-list">
            <li><i class="ti ti-circle-check"></i> Strengths &amp; areas for improvement</li>
            <li><i class="ti ti-circle-check"></i> Targeted feedback to help your child excel</li>
          </ul>
        </div>
        <div class="k-rar-image">
          <img src="https://images.pexels.com/photos/7054406/pexels-photo-7054406.jpeg" alt="Sehyog Academy Result Analysis Report on laptop screen">
        </div>
      </div>
    </div>
  </section>

  <!-- ============ SUBJECTS & PRICING ============ -->
  <section class="k-section alt">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar" style="background:var(--k-navy-light);"></span>
        <span class="label">Subject &amp; Course Offered</span>
      </div>
      <h2 class="k-title">Choose the Plan That Fits Your Family</h2>
      <p class="k-sub">Core subjects and languages covered, with two flexible ways to enrol.</p>

      <div class="k-tag-row">
        <span class="k-tag"><i class="ti ti-math-symbols"></i> Maths</span>
        <span class="k-tag"><i class="ti ti-book"></i> English (Literature + Language)</span>
        <span class="k-tag"><i class="ti ti-atom-2"></i> Physics</span>
        <span class="k-tag"><i class="ti ti-flask"></i> Chemistry</span>
        <span class="k-tag"><i class="ti ti-microscope"></i> Biology</span>
        <span class="k-tag lang"><i class="ti ti-language"></i> French</span>
        <span class="k-tag lang"><i class="ti ti-language"></i> German</span>
        <span class="k-tag lang"><i class="ti ti-language"></i> Spanish</span>
      </div>

      <div class="k-pack-grid">
        <div class="k-pack-card k-reveal">
          <div class="k-pack-head">
            <span class="save-tag">Save up to 20%</span>
            <h3>Eco Pack — 35–46 Week Commitment</h3>
          </div>
          <p class="k-pack-sub">Basics to excellence — comprehensive preparation for Year 7, 8 &amp; 9 students</p>
          <div class="k-pack-body">
            <div class="k-pack-row"><span class="k-lbl">Recommended for</span><span class="k-val">Year 7, 8 &amp; 9</span></div>
            <div class="k-pack-row"><span class="k-lbl">Duration</span><span class="k-val">35–46 Weeks</span></div>
            <div class="k-pack-row"><span class="k-lbl">Sessions</span><span class="k-val">184–230 Sessions</span></div>
            <p class="k-pack-provided">
              Course structure &amp; pre-lecture material (PLM), daily/weekly home, class &amp; face-to-face unit/topic tests, fortnightly revision tests, and weekly doubt-clearing sessions.
            </p>
            <ul class="k-pack-fee-list">
              <li>Advance one-time payment or monthly payment</li>
              <li>Discount up to 20% — contact us for details</li>
            </ul>
            <span class="k-pack-cta"><i class="ti ti-file-text"></i> Weekly Practice Papers Included</span>
          </div>
        </div>

        <div class="k-pack-card k-reveal">
          <div class="k-pack-head"><h3>Pay As You Go Pack</h3></div>
          <p class="k-pack-sub">Basics to best-level GCSE-track preparation for Year 7, 8 &amp; 9 students</p>
          <div class="k-pack-body">
            <div class="k-pack-row"><span class="k-lbl">Recommended for</span><span class="k-val">Year 7, 8 &amp; 9</span></div>
            <div class="k-pack-row"><span class="k-lbl">Duration</span><span class="k-val">Flexible</span></div>
            <div class="k-pack-row"><span class="k-lbl">Sessions</span><span class="k-val">Flexible</span></div>
            <p class="k-pack-provided">
              Course structure &amp; pre-lecture material (PLM), daily/weekly home, class &amp; topic tests, revision tests, and quick revision &amp; doubt-clearing sessions.
            </p>
            <ul class="k-pack-fee-list">
              <li>Group of 5 — £16.99 / hour*</li>
              <li>Group of 3 — £18.99 / hour*</li>
              <li>Group of 2 — £24.99 / hour*</li>
              <li>One to One — £29.99 / hour*</li>
            </ul>
            <span class="k-pack-cta"><i class="ti ti-file-text"></i> Weekly Practice Papers Included</span>
          </div>
        </div>
      </div>
      <p style="font-size:13px;color:var(--k-text-soft);margin-top:22px;">
        Other subjects available on demand, including Computer Science and Geography. * Terms &amp; conditions apply.
      </p>
    </div>
  </section>

  <!-- ============ YEAR 9 COURSE BREAKDOWN ============ -->
  <section class="k-section">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar" style="background:var(--k-cyan-dark);"></span>
        <span class="label">Sample Structure — Year 9</span>
      </div>
      <h2 class="k-title">Comprehensive Course Offerings</h2>
      <p class="k-sub">A look at what a full year of KS3 study covers across all five core subjects.</p>

      <div class="k-breakdown k-reveal">
        <div class="k-breakdown-left">
          <div class="k-breakdown-circle">
            <span class="big">205</span>
            <span class="small">Sessions covering all 5 subjects</span>
          </div>
        </div>
        <div class="k-breakdown-right">
          <div class="k-subject-chip">46 Maths Classes</div>
          <div class="k-subject-chip">46 English Classes</div>
          <div class="k-subject-chip">30 Physics Classes</div>
          <div class="k-subject-chip">30 Chemistry Classes</div>
          <div class="k-subject-chip">30 Biology Classes</div>
          <div class="k-subject-chip">23 Doubt Clearing Sessions</div>
        </div>
      </div>

      <div class="k-includes-box">
        <ul>
          <li><i class="ti ti-notes"></i> Pre-lecture notes for every class</li>
          <li><i class="ti ti-clipboard-check"></i> 136 weekly online unit/topic tests</li>
          <li><i class="ti ti-home-2"></i> 136 weekly home assignments (30 each subject)</li>
          <li><i class="ti ti-users"></i> 10 parent-teacher meetings</li>
          <li><i class="ti ti-report-analytics"></i> Comprehensive Result Analysis Report (RAR)</li>
          <li><i class="ti ti-list-check"></i> Error log, analysis, consulting &amp; weak/strong area identification</li>
        </ul>
      </div>
    </div>
  </section>

  <!-- ============ WEEKLY TIMETABLE ============ -->
  <section class="k-section alt">
    <div class="k-wrap">
      <div class="k-section-head">
        <span class="bar"></span>
        <span class="label">Weekly Course Structure</span>
      </div>
      <h2 class="k-title">A Typical Week at Sehyog</h2>
      <p class="k-sub">Flexible after-school timing, whether your child is on the full multi-subject track or focusing on a single subject.</p>

      <div class="k-timetable-grid">
        <div class="k-timetable-card k-reveal">
          <div class="k-timetable-head">Multi-Subject Track (Year 9 Example)</div>
          <div class="k-day-row"><span class="k-day-badge">MON</span><div class="k-day-info"><div class="k-day-title">Maths Class (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">TUE</span><div class="k-day-info"><div class="k-day-title">English Class (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">WED</span><div class="k-day-info"><div class="k-day-title">Physics / Chemistry / Biology (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">THU</span><div class="k-day-info"><div class="k-day-title">Physics / Chemistry Class (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">FRI</span><div class="k-day-info"><div class="k-day-title">Biology / Doubt + Feedback Sessions (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row k-online"><span class="k-day-badge">SAT</span><div class="k-day-info"><div class="k-day-title">Assignment / Homework (Online)</div></div></div>
          <div class="k-day-row k-online"><span class="k-day-badge">SUN</span><div class="k-day-info"><div class="k-day-title">Unit / Topic Test (Online)</div><div class="k-day-time">AQA / Edexcel / OCR / Eduqas — anywhere, anytime</div></div></div>
        </div>

        <div class="k-timetable-card k-reveal">
          <div class="k-timetable-head">Single-Subject Track</div>
          <div class="k-day-row"><span class="k-day-badge">MON</span><div class="k-day-info"><div class="k-day-title">Opted Subject (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">TUE</span><div class="k-day-info"><div class="k-day-title">Opted Subject (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">WED</span><div class="k-day-info"><div class="k-day-title">Opted Subject (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">THU</span><div class="k-day-info"><div class="k-day-title">Opted Subject (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row"><span class="k-day-badge">FRI</span><div class="k-day-info"><div class="k-day-title">Doubt / Feedback Session — Alternate (1 Hour)</div><div class="k-day-time">Flexible timing after school*</div></div></div>
          <div class="k-day-row k-online"><span class="k-day-badge">SAT</span><div class="k-day-info"><div class="k-day-title">Assignment / Homework (Online)</div></div></div>
          <div class="k-day-row k-online"><span class="k-day-badge">SUN</span><div class="k-day-info"><div class="k-day-title">Unit / Topic Test (Online)</div><div class="k-day-time">AQA / Edexcel / OCR / Eduqas — anywhere, anytime</div></div></div>
        </div>
      </div>
      <p style="font-size:13px;color:var(--k-text-soft);margin-top:20px;">* Terms &amp; conditions apply.</p>
    </div>
  </section>